/**
 * Kora API Client
 * Typed client for Kora identity and access management service
 */
import { BaseApiClient } from '../client/base-client.js';
/**
 * Kora API Client
 */
export class KoraClient extends BaseApiClient {
    constructor(config) {
        super({
            ...config,
            baseURL: config.baseURL || 'https://api.pinakes.de'
        });
    }
    externalSourcePath(sourceId) {
        return `/api/v1/admin/external-entitlements/sources/${encodeURIComponent(sourceId)}`;
    }
    externalRulePath(sourceId, ruleId) {
        return `${this.externalSourcePath(sourceId)}/grant-rules/${encodeURIComponent(ruleId)}`;
    }
    async listExternalEntitlementSources(query = {}) {
        return this.get('/api/v1/admin/external-entitlements/sources', { params: query });
    }
    async createExternalEntitlementSource(input) {
        return this.post('/api/v1/admin/external-entitlements/sources', input);
    }
    async getExternalEntitlementSource(sourceId) {
        return this.get(this.externalSourcePath(sourceId));
    }
    async updateExternalEntitlementSource(sourceId, input) {
        return this.put(this.externalSourcePath(sourceId), input);
    }
    async listExternalGrantRules(sourceId, query = {}) {
        return this.get(`${this.externalSourcePath(sourceId)}/grant-rules`, { params: query });
    }
    async getExternalGrantRule(sourceId, ruleId) {
        return this.get(this.externalRulePath(sourceId, ruleId));
    }
    async createExternalGrantRule(sourceId, input) {
        return this.post(`${this.externalSourcePath(sourceId)}/grant-rules`, input);
    }
    async replaceExternalGrantRule(sourceId, ruleId, input) {
        return this.put(this.externalRulePath(sourceId, ruleId), input);
    }
    async retireExternalGrantRule(sourceId, ruleId) {
        return this.post(`${this.externalRulePath(sourceId, ruleId)}/retire`, {});
    }
    async importExternalRoster(sourceId, csv, format) {
        return this.post(`${this.externalSourcePath(sourceId)}/roster-imports?format=${encodeURIComponent(format)}`, csv, { headers: { 'Content-Type': 'text/csv' } }, { retries: 0, retryDelay: 0 });
    }
    async importOrganizationMembers(organizationId, csv, dryRun, options = {}) {
        const query = new URLSearchParams({ dryRun: String(dryRun) });
        if (options.sendInvitations) {
            query.set('sendInvitations', 'true');
            if (options.invitationLocale)
                query.set('invitationLocale', options.invitationLocale);
        }
        return this.post(`/api/v1/admin/organizations/${encodeURIComponent(organizationId)}/member-imports?${query}`, csv, { headers: { 'Content-Type': 'text/csv' } }, { retries: 0, retryDelay: 0 });
    }
    async listExternalRosterImports(sourceId, query = {}) {
        return this.get(`${this.externalSourcePath(sourceId)}/roster-imports`, { params: query });
    }
    async getClaimableMembershipSources() {
        return this.get('/api/v1/membership/sources');
    }
    async getMyMembershipClaims() {
        return this.get('/api/v1/membership/claims');
    }
    async createMembershipClaim(sourceId, email) {
        return this.post('/api/v1/membership/claims', { sourceId, email });
    }
    async resendMembershipConfirmation(claimId) {
        return this.post(`/api/v1/membership/claims/${encodeURIComponent(claimId)}/confirmation`, {});
    }
    async confirmMembershipClaim(claimId, token) {
        return this.post(`/api/v1/membership/claims/${encodeURIComponent(claimId)}/confirm`, { token });
    }
    async withdrawMembershipClaim(claimId) {
        await this.delete(`/api/v1/membership/claims/${encodeURIComponent(claimId)}`);
    }
    // ============================================================================
    // USER MANAGEMENT
    // ============================================================================
    /**
     * Get current user profile
     * GET /api/v1/users/me
     */
    async getCurrentUser() {
        return this.get('/api/v1/users/me');
    }
    /**
     * Record SPA logout in Kora (`AUTH_LOGOUT`) while the access token is still valid.
     * Call immediately before discarding tokens locally if Auth0 log streaming is not used.
     * POST /api/v1/auth/logout — responds with 204 No Content.
     */
    async recordClientLogout() {
        await this.post('/api/v1/auth/logout', undefined);
    }
    /**
     * Create current user profile (self-registration)
     * POST /api/v1/users/me
     */
    async createCurrentUser(data) {
        return this.post('/api/v1/users/me', data);
    }
    /**
     * Update current user profile
     * PUT /api/v1/users/me
     */
    async updateCurrentUser(data) {
        return this.put('/api/v1/users/me', data);
    }
    /**
     * Get current user's trust evaluation
     * GET /api/v1/users/me/trust-evaluation
     */
    async getTrustEvaluation() {
        return this.get('/api/v1/users/me/trust-evaluation');
    }
    /**
     * Get current user's organizations
     * GET /api/v1/organizations/me
     */
    async getMyOrganizations() {
        const response = await this.get('/api/v1/organizations/me');
        return response.organizations;
    }
    /**
     * Get current user's organizations with themes
     * GET /api/v1/organizations/me
     */
    async getMyOrganizationsWithThemes() {
        return this.get('/api/v1/organizations/me');
    }
    // ============================================================================
    // ORGANIZATIONS
    // ============================================================================
    // KORA-204: `listOrganizations`, `getOrganization` and `getOrganizationMembers` called
    // `/api/v1/organizations`, `/organizations/:id` and `/organizations/:id/members`, which Kora never
    // served, and nothing called them. Use `listAdminOrganizations` (`/admin/organizations`) and
    // `listOrganizationMembers` (`/orgs/:orgId/members`).
    /**
     * Get organization theme
     * GET /api/v1/organizations/:orgId/theme
     */
    async getOrganizationTheme(orgId) {
        return this.get(`/api/v1/organizations/${orgId}/theme`);
    }
    /**
     * Get organization theme with inheritance
     * GET /api/v1/organizations/:orgId/theme/inherited
     */
    async getOrganizationThemeInherited(orgId) {
        return this.get(`/api/v1/organizations/${orgId}/theme/inherited`);
    }
    // ============================================================================
    // ADMIN ENDPOINTS (Require admin permissions)
    // ============================================================================
    /**
     * List all users (admin only)
     * GET /api/v1/admin/users
     */
    async listUsers(params) {
        return this.get('/api/v1/admin/users', {
            params
        });
    }
    /**
     * Get user by ID (admin only)
     * GET /api/v1/admin/users/:id
     */
    async getUser(id) {
        return this.get(`/api/v1/admin/users/${id}`);
    }
    /**
     * Update user (admin only)
     * PUT /api/v1/admin/users/:id
     *
     * `auth0Id` is accepted only when the user still has a `kora-pending:…` placeholder;
     * set it to the real Auth0 `sub` to link the record.
     */
    async updateUser(id, data) {
        return this.put(`/api/v1/admin/users/${id}`, data);
    }
    /**
     * List active product grants for a user.
     * GET /api/v1/users/:id/grants
     */
    async listUserGrants(id) {
        const response = await this.get(`/api/v1/users/${id}/grants`);
        return response.grants;
    }
    /**
     * List active product grants for the current authenticated user.
     * GET /api/v1/users/me/grants
     */
    async listCurrentUserGrants() {
        const response = await this.get('/api/v1/users/me/grants');
        return response.grants;
    }
    /**
     * The current user's active grants as a server-resolved rights view (KORA-115): plain-language
     * scope, origin, and whether the user may revoke each one.
     * GET /api/v1/users/me/rights
     */
    async listMyRights() {
        const response = await this.get('/api/v1/users/me/rights');
        return response.rights;
    }
    async listMyConsents() {
        const response = await this.get('/api/v1/users/me/consents');
        return response.consents;
    }
    async requestConsentApprovalLink(consentId) {
        return this.post(`/api/v1/users/me/consents/${encodeURIComponent(consentId)}/approval-link`);
    }
    async approveConsent(consentId, input) {
        await this.post(`/api/v1/users/me/consents/${encodeURIComponent(consentId)}/approve`, input);
    }
    async declineConsent(consentId) {
        await this.post(`/api/v1/users/me/consents/${encodeURIComponent(consentId)}/decline`);
    }
    async withdrawConsent(consentId) {
        await this.post(`/api/v1/users/me/consents/${encodeURIComponent(consentId)}/withdraw`);
    }
    // ============================================================================
    // MEMBER PROFILES (KORA-123 / KORA-132), all self only
    // ============================================================================
    /** GET /api/v1/users/me/profiles */
    async listMyProfiles() {
        const response = await this.get('/api/v1/users/me/profiles');
        return response.profiles;
    }
    /** GET /api/v1/users/me/profiles/:kind; 404 `KORA_DOMAIN_PROFILE_NOT_FOUND` when there is none. */
    async getMyProfile(kind) {
        return this.get(`/api/v1/users/me/profiles/${encodeURIComponent(kind)}`);
    }
    /** PUT /api/v1/users/me/profiles/:kind; 409 on a stale `baseRevision`, 400 on invalid sections. */
    async saveMyProfile(kind, input) {
        return this.put(`/api/v1/users/me/profiles/${encodeURIComponent(kind)}`, input);
    }
    /** DELETE /api/v1/users/me/profiles/:kind (also removes its disclosures). */
    async deleteMyProfile(kind) {
        await this.delete(`/api/v1/users/me/profiles/${encodeURIComponent(kind)}`);
    }
    /** GET /api/v1/users/me/organizations/:orgId/overlay */
    async listMyOverlay(organizationId) {
        const response = await this.get(`/api/v1/users/me/organizations/${encodeURIComponent(organizationId)}/overlay`);
        return response.fields;
    }
    /** PUT /api/v1/users/me/organizations/:orgId/overlay/:fieldKey */
    async setMyOverlayValue(organizationId, fieldKey, value) {
        await this.put(this.overlayPath(organizationId, fieldKey), { value });
    }
    /** DELETE /api/v1/users/me/organizations/:orgId/overlay/:fieldKey */
    async removeMyOverlayValue(organizationId, fieldKey) {
        await this.delete(this.overlayPath(organizationId, fieldKey));
    }
    /** PUT /api/v1/users/me/organizations/:orgId/overlay/:fieldKey/share (member-shareable fields) */
    async shareMyOverlayValue(organizationId, fieldKey) {
        await this.put(`${this.overlayPath(organizationId, fieldKey)}/share`);
    }
    /** DELETE /api/v1/users/me/organizations/:orgId/overlay/:fieldKey/share */
    async unshareMyOverlayValue(organizationId, fieldKey) {
        await this.delete(`${this.overlayPath(organizationId, fieldKey)}/share`);
    }
    /** POST /api/v1/users/me/organizations/:orgId/overlay/:fieldKey/confirm (accept a widened class) */
    async confirmMyOverlayValue(organizationId, fieldKey) {
        await this.post(`${this.overlayPath(organizationId, fieldKey)}/confirm`);
    }
    /** GET /api/v1/users/me/profile-visibility: every membership with its overlay audience and disclosures. */
    async getMyProfileVisibility() {
        const response = await this.get('/api/v1/users/me/profile-visibility');
        return response.organizations;
    }
    /** PUT /api/v1/users/me/disclosures (idempotent) */
    async grantMyDisclosure(input) {
        await this.put('/api/v1/users/me/disclosures', input);
    }
    /** DELETE /api/v1/users/me/disclosures (idempotent) */
    async withdrawMyDisclosure(input) {
        await this.delete('/api/v1/users/me/disclosures', input);
    }
    /** GET /api/v1/users/me/age-attestation */
    async getMyAgeAttestation() {
        return this.get('/api/v1/users/me/age-attestation');
    }
    /** GET /api/v1/orgs/:orgId/admin-acknowledgement: the confidentiality statement a club administrator accepts, with when the caller accepted it (KORA-160/167). */
    async getAdminAcknowledgement(organizationId) {
        return this.get(`/api/v1/orgs/${encodeURIComponent(organizationId)}/admin-acknowledgement`);
    }
    /**
     * GET /api/v1/orgs/:orgId/members/:userId/export (KORA-161): one member's data held for this
     * organization and the circles below it, for an organization owner or a platform administrator.
     * Kora records the read in the member's access record before it answers.
     */
    async exportOrganizationMemberData(organizationId, userId) {
        return this.get(`/api/v1/orgs/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/export`);
    }
    /** POST /api/v1/orgs/:orgId/admin-acknowledgement: accept the named statement version (idempotent). */
    async acceptAdminAcknowledgement(organizationId, statementVersion) {
        return this.post(`/api/v1/orgs/${encodeURIComponent(organizationId)}/admin-acknowledgement`, { statementVersion });
    }
    /** GET /api/v1/orgs/:orgId/members (organization administrators and platform admins; the read is audited) */
    async listOrganizationMembers(organizationId) {
        return this.get(`/api/v1/orgs/${encodeURIComponent(organizationId)}/members`);
    }
    /**
     * POST /api/v1/orgs/:orgId/members: set a member's role (KORA-182). A club owner may set manager
     * or member; only a platform admin may set owner. 409 KORA_LAST_OWNER when no owner would remain.
     */
    async setOrganizationMemberRole(organizationId, userId, role) {
        await this.post(`/api/v1/orgs/${encodeURIComponent(organizationId)}/members`, { userId, role });
    }
    /** GET /api/v1/legal/terms (public): the current Terms version and whether the gate is on (KORA-189). */
    async getCurrentTerms() {
        return this.get('/api/v1/legal/terms');
    }
    /** GET /api/v1/users/me/terms-acceptance */
    async getMyTermsAcceptance() {
        return this.get('/api/v1/users/me/terms-acceptance');
    }
    /** PUT /api/v1/users/me/terms-acceptance; 409 KORA_TERMS_VERSION_MISMATCH when `version` is not current. */
    async acceptMyTerms(version, locale) {
        return this.put('/api/v1/users/me/terms-acceptance', { version, locale });
    }
    /** PUT /api/v1/users/me/age-attestation (idempotent; the subject is always the caller) */
    async attestMyAge() {
        return this.put('/api/v1/users/me/age-attestation');
    }
    /** GET /api/v1/users/me/access-record; `before` is a `YYYY-MM-DD` day from a previous page. */
    async getMyAccessRecord(before) {
        return this.get('/api/v1/users/me/access-record', {
            ...(before ? { params: { before } } : {})
        });
    }
    overlayPath(organizationId, fieldKey) {
        return `/api/v1/users/me/organizations/${encodeURIComponent(organizationId)}/overlay/${encodeURIComponent(fieldKey)}`;
    }
    // ============================================================================
    // ACCOUNT DATA (KORA-125)
    // ============================================================================
    /**
     * Download everything Kora holds about the signed-in person. Needs a recent sign-in: a 401 with
     * code `KORA_REAUTH_REQUIRED` means "sign in again" (see `error.details.reason`). Rate limited
     * to 5 per hour.
     * GET /api/v1/users/me/data-export
     */
    async exportMyData() {
        return this.get('/api/v1/users/me/data-export');
    }
    /**
     * The caller's open deletion request, what deleting would remove, and whether self-service
     * deletion is available.
     * GET /api/v1/users/me/deletion
     */
    async getMyDeletion() {
        return this.get('/api/v1/users/me/deletion');
    }
    /**
     * Start a deletion request; a confirmation link is emailed. 404 `KORA_DELETION_DISABLED` while
     * self-service is off, 409 when one is open or the caller is a platform administrator.
     * `publishedWork` is the member's choice about their published work (KORA-174). There is no
     * default: the server refuses a request without it. Asking again while a request is pending
     * replaces the choice.
     * POST /api/v1/users/me/deletion
     */
    async requestMyDeletion(publishedWork) {
        return this.post('/api/v1/users/me/deletion', {
            acknowledged: true,
            ...publishedWork
        });
    }
    /**
     * Confirm with the id and secret from the emailed link. Needs a recent sign-in.
     * POST /api/v1/users/me/deletion/confirm
     */
    async confirmMyDeletion(deletionId, token) {
        return this.post('/api/v1/users/me/deletion/confirm', {
            deletionId,
            token
        });
    }
    /**
     * Cancel the pending or scheduled request. 409 `KORA_DELETION_NOT_CANCELLABLE` once the purge
     * has started.
     * POST /api/v1/users/me/deletion/cancel
     */
    async cancelMyDeletion() {
        await this.post('/api/v1/users/me/deletion/cancel');
    }
    /**
     * Revoke one of the current user's own grants. 204 on success; 403 with code
     * KORA_GRANT_NOT_SELF_REVOCABLE when the grant is not self-revocable (see `listMyRights`).
     * DELETE /api/v1/users/me/grants/:grantId
     */
    async revokeMyGrant(grantId) {
        await this.delete(`/api/v1/users/me/grants/${encodeURIComponent(grantId)}`);
    }
    /**
     * Issue a product grant for a user. Create-only: each call appends a new ledger row and
     * returns its id. Re-issuing after a mistake means revoking the old grant and issuing a new
     * one — nothing here updates or revives an existing row.
     * POST /api/v1/users/:id/grants
     */
    async issueUserGrant(id, data) {
        return this.post(`/api/v1/users/${id}/grants`, data);
    }
    /**
     * Revoke one product grant by its id. Kora returns 204 No Content, and is idempotent: revoking
     * an already-revoked grant succeeds without moving its original revocation time.
     * DELETE /api/v1/users/:id/grants/:grantId
     */
    async revokeUserGrant(id, grantId) {
        await this.delete(`/api/v1/users/${id}/grants/${encodeURIComponent(grantId)}`);
    }
    /**
     * Create user (admin only)
     * POST /api/v1/admin/users
     */
    async createUser(data) {
        return this.post('/api/v1/admin/users', data);
    }
    /**
     * Erase a person's account on their request (admin only, KORA-125). Replaces the former
     * `deleteUser`, which removed only the Kora row. `immediate` is accepted only with the reason
     * `member_request_verified`; otherwise the erasure waits out the grace period and the member can
     * still cancel. 202 with the purge date; 409 when a request is already open or the target is a
     * platform administrator (demote first).
     * POST /api/v1/admin/users/:id/erasure
     */
    async eraseUser(userId, input) {
        return this.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/erasure`, input);
    }
    /**
     * Cancel a pending or scheduled erasure (admin only). 409 once the purge has started.
     * POST /api/v1/admin/users/:id/erasure/cancel
     */
    async cancelUserErasure(userId) {
        await this.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/erasure/cancel`);
    }
    // ============================================================================
    // PRODUCT STAFF PERMISSIONS (Admin only, KORA-184)
    // ============================================================================
    /**
     * The staff permissions Kora can grant (the catalog in @architeia/types).
     * GET /api/v1/admin/staff-permissions/catalog
     */
    async getStaffPermissionCatalog() {
        const response = await this.get('/api/v1/admin/staff-permissions/catalog');
        return response.permissions;
    }
    /**
     * Every staff permission grant of one user, active and ended.
     * GET /api/v1/admin/users/:id/staff-permissions
     */
    async listStaffPermissionGrants(userId) {
        const response = await this.get(`/api/v1/admin/users/${encodeURIComponent(userId)}/staff-permissions`);
        return response.grants;
    }
    /**
     * Grant one staff permission. 409 when the user already holds it.
     * POST /api/v1/admin/users/:id/staff-permissions
     */
    async grantStaffPermission(userId, input) {
        return this.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/staff-permissions`, input);
    }
    /**
     * The code-defined staff permission templates (KORA-197).
     * GET /api/v1/admin/staff-permission-templates
     */
    async getStaffPermissionTemplates() {
        const response = await this.get('/api/v1/admin/staff-permission-templates');
        return response.templates;
    }
    /**
     * Apply a template: grants every permission the user does not hold yet, in one transaction.
     * POST /api/v1/admin/users/:id/staff-permission-templates/:templateId
     */
    async applyStaffPermissionTemplate(userId, templateId, input = {}) {
        return this.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/staff-permission-templates/${encodeURIComponent(templateId)}`, input);
    }
    /**
     * Revoke one staff permission grant (the row stays, marked revoked).
     * DELETE /api/v1/admin/users/:id/staff-permissions/:grantId
     */
    async revokeStaffPermission(userId, grantId) {
        return this.delete(`/api/v1/admin/users/${encodeURIComponent(userId)}/staff-permissions/${encodeURIComponent(grantId)}`);
    }
    // ============================================================================
    // THEME MANAGEMENT (Admin only)
    // ============================================================================
    /**
     * Get organization theme (admin)
     * GET /api/v1/admin/organizations/:orgId/theme
     */
    async getAdminOrganizationTheme(orgId) {
        return this.get(`/api/v1/admin/organizations/${orgId}/theme`);
    }
    /**
     * Update organization theme (admin)
     * PUT /api/v1/admin/organizations/:orgId/theme
     */
    async updateOrganizationTheme(orgId, theme) {
        return this.put(`/api/v1/admin/organizations/${orgId}/theme`, { theme });
    }
    /**
     * Delete organization theme (admin)
     * DELETE /api/v1/admin/organizations/:orgId/theme
     */
    async deleteOrganizationTheme(orgId) {
        return this.delete(`/api/v1/admin/organizations/${orgId}/theme`);
    }
    // ============================================================================
    // MFA (admin)
    // ============================================================================
    //
    // KORA-204: the only MFA calls this client makes are the administrator's, and they match the
    // routes Kora serves (`admin-mfa.routes.ts`). The earlier "self" variants and `enable` called
    // routes that never existed; a member's own MFA flow is `/users/me/mfa/*` and has no client yet.
    /**
     * One user's MFA state, as an administrator sees it.
     * GET /api/v1/admin/users/:userId/mfa
     */
    async getUserMfaStatus(userId) {
        return this.get(`/api/v1/admin/users/${encodeURIComponent(userId)}/mfa`);
    }
    /**
     * Disable a user's MFA. The reason is mandatory (at least
     * `ADMIN_MFA_DISABLE_REASON_MIN_LENGTH` characters) and is written to the audit trail.
     * DELETE /api/v1/admin/users/:userId/mfa
     */
    async disableUserMfa(userId, reason) {
        const body = { reason };
        await this.delete(`/api/v1/admin/users/${encodeURIComponent(userId)}/mfa`, body);
    }
    // ============================================================================
    // AUDIT LOGS
    // ============================================================================
    /**
     * Get audit logs (admin only)
     * GET /api/v1/audit/logs
     */
    async getAuditLogs(params) {
        return this.get('/api/v1/audit/logs', {
            params
        });
    }
    /**
     * Get security events (admin only)
     * GET /api/v1/audit/security-events
     */
    async getSecurityEvents(params) {
        return this.get('/api/v1/audit/security-events', {
            params
        });
    }
    /**
     * Get audit log statistics (admin only)
     * GET /api/v1/audit/logs/stats
     */
    async getAuditLogStats(params) {
        return this.get('/api/v1/audit/logs/stats', {
            params
        });
    }
    /**
     * Get security event statistics (admin only)
     * GET /api/v1/audit/security-events/stats
     */
    async getSecurityEventStats(params) {
        return this.get('/api/v1/audit/security-events/stats', {
            params
        });
    }
    // ============================================================================
    // SECURITY MONITORING (Admin only)
    // ============================================================================
    /**
     * Get rate limiting statistics (admin only)
     * GET /api/v1/security/rate-limits/stats
     */
    async getRateLimitStats() {
        return this.get('/api/v1/security/rate-limits/stats');
    }
    /**
     * Reset rate limit for a key (admin only)
     * POST /api/v1/security/rate-limits/reset
     */
    async resetRateLimit(key) {
        return this.post('/api/v1/security/rate-limits/reset', { key });
    }
    /**
     * Get anomaly detection statistics (admin only)
     * GET /api/v1/security/anomalies/stats
     */
    async getAnomalyStats(params) {
        return this.get('/api/v1/security/anomalies/stats', {
            params
        });
    }
    /**
     * Get security overview (admin only)
     * GET /api/v1/security/overview
     */
    async getSecurityOverview() {
        return this.get('/api/v1/security/overview');
    }
    // ============================================================================
    // ADMIN SESSION MANAGEMENT
    // ============================================================================
    /**
     * Get user sessions (admin only)
     * GET /api/v1/sessions/user/:userId
     */
    async getUserSessions(userId, params) {
        return this.get(`/api/v1/sessions/user/${userId}`, {
            params
        });
    }
    /**
     * Revoke user session (admin only)
     * DELETE /api/v1/sessions/:sessionId
     */
    async revokeUserSession(sessionId, reason) {
        return this.delete(`/api/v1/sessions/${sessionId}`, {
            data: { reason }
        });
    }
    /**
     * Revoke all user sessions (admin only)
     * POST /api/v1/sessions/user/:userId/revoke-all
     */
    async revokeAllUserSessions(userId, reason) {
        return this.post(`/api/v1/sessions/user/${userId}/revoke-all`, { reason });
    }
    /**
     * Get global session statistics (admin only)
     * GET /api/v1/sessions/stats
     */
    async getSessionStats() {
        return this.get('/api/v1/sessions/stats');
    }
    // ============================================================================
    // ORGANIZATION MANAGEMENT (Admin only)
    // ============================================================================
    /**
     * List all organizations with member counts, no member rows (admin only, KORA-183)
     * GET /api/v1/admin/organizations
     */
    async listAdminOrganizations() {
        return this.get('/api/v1/admin/organizations');
    }
    /**
     * Create organization (admin only)
     * POST /api/v1/admin/organizations
     */
    async createOrganization(data) {
        return this.post('/api/v1/admin/organizations', data);
    }
    /**
     * Update organization (admin only)
     * PUT /api/v1/admin/organizations/:id
     */
    async updateOrganization(id, data) {
        return this.put(`/api/v1/admin/organizations/${id}`, data);
    }
    /**
     * Delete organization (admin only)
     * DELETE /api/v1/admin/organizations/:id
     */
    async deleteOrganization(id) {
        return this.delete(`/api/v1/admin/organizations/${id}`);
    }
    // ============================================================================
    // HEALTH CHECKS
    // ============================================================================
    /**
     * Health check
     * GET /health
     */
    async healthCheck() {
        return this.get('/health');
    }
    /**
     * User service health check
     * GET /api/v1/users/health
     */
    async userServiceHealth() {
        return this.get('/api/v1/users/health');
    }
}
